/// FOURJS_START_COPYRIGHT(D,2014)
/// Property of Four Js*
/// (c) Copyright Four Js 2014, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT
// noinspection JSUnresolvedReference

"use strict";

modulum("ModelHelper", [], function (context, cls) {
  /**
   * Helper to ease AUI tree access for customized widgets,
   * Manages client side life cycle representation of the node.
   * @class ModelHelper
   * @memberOf classes
   * @publicdoc Base
   */
  class ModelHelper {
    constructor(widget) {
      /** @type {classes.WidgetBase} */
      this._widget = widget;
      /** @type {?Function[]} */
      this._newApplicationsListeners = null;
      /** @type {?Function[]} */
      this._closeApplicationsListeners = null;
      /** @type {?Function[]} */
      this._sessionEndListeners = null;
      /** @type {?Function[]} */
      this._auiUpdateListeners = null;
      /** @type {?Function[]} */
      this._currentWindowListeners = null;
      /** @type {?Function[]} */
      this._activeListenersHandlers = null;
    }

    /**
     * Unbind all active listeners and destroy the current instance.
     */
    destroy() {
      // unbind all active listeners
      let h = null;
      if (this._activeListenersHandlers) {
        while (this._activeListenersHandlers.length > 0) {
          h = this._activeListenersHandlers.pop();
          if (h) {
            h(); // unregister listener
          }
        }
        h = null;
        this._activeListenersHandlers = null;
      }
      this._newApplicationsListeners = null;
      this._closeApplicationsListeners = null;
      this._sessionEndListeners = null;
      this._auiUpdateListeners = null;
      this._currentWindowListeners = null;

      this._widget = null;
    }

    /**
     * Registers a listener which will be called when a new application is started
     * @param listener {Function=} - the function to call when a new application is started
     * @returns {function(this:classes.ModelHelper)} The unregister handler. Simply call this function to stop listening for new applications
     * @publicdoc
     */
    addNewApplicationListener(listener) {
      if (this._activeListenersHandlers === null) {
        this._activeListenersHandlers = [];
      }
      if (this._newApplicationsListeners === null) {
        this._newApplicationsListeners = [];

        const onApplicationAdded = function (event, sender, app) {
          // for each iteration we recheck if array is defined because modelhelper may have been destroyed in a previous iteration
          for (let i = 0; this._newApplicationsListeners && i < this._newApplicationsListeners.length; ++i) {
            this._newApplicationsListeners[i](app);
          }
        }.bind(this);

        this._activeListenersHandlers.push(
          context.SessionService.onSessionAdded(
            function (event, sender, session) {
              // If null, widget may have been destroyed. We stop to create listeners. Otherwise, we attach new one.
              if (this._activeListenersHandlers) {
                this._activeListenersHandlers.push(session.getNavigationManager().onApplicationAdded(onApplicationAdded));
              }
            }.bind(this)
          )
        );
        const currentSession = context.SessionService.getCurrent();
        if (currentSession) {
          this._activeListenersHandlers.push(currentSession.getNavigationManager().onApplicationAdded(onApplicationAdded));
        }
      }
      this._newApplicationsListeners.push(listener);

      return () => {
        if (this._newApplicationsListeners) {
          cls.ArrayHelper.remove(this._newApplicationsListeners, listener);
        }
      };
    }

    /**
     * Registers a listener which will be called when an application is closed
     * @param listener {Function=} - the function to call when an application is closed
     * @returns {function(this:classes.ModelHelper)} The unregister handler. Simply call this function to stop listening for closed applications
     * @publicdoc
     */
    addCloseApplicationListener(listener) {
      if (this._activeListenersHandlers === null) {
        this._activeListenersHandlers = [];
      }
      if (this._closeApplicationsListeners === null) {
        this._closeApplicationsListeners = [];

        const onApplicationClosed = (event, sender, app) => {
          // for each iteration we recheck if array is defined because modelhelper may have been destroyed in a previous iteration
          for (let i = 0; this._closeApplicationsListeners && i < this._closeApplicationsListeners.length; ++i) {
            this._closeApplicationsListeners[i](app);
          }
        };

        this._activeListenersHandlers.push(
          context.SessionService.onSessionAdded((event, sender, session) => {
            // If null, widget may have been destroyed. We stop to create listeners. Otherwise, we attach new one.
            if (this._activeListenersHandlers) {
              this._activeListenersHandlers.push(session.getNavigationManager().onApplicationRemoved(onApplicationClosed));
            }
          })
        );
        const currentSession = context.SessionService.getCurrent();
        if (currentSession) {
          this._activeListenersHandlers.push(currentSession.getNavigationManager().onApplicationRemoved(onApplicationClosed));
        }
      }
      this._closeApplicationsListeners.push(listener);

      return () => {
        if (this._closeApplicationsListeners) {
          cls.ArrayHelper.remove(this._closeApplicationsListeners, listener);
        }
      };
    }

    /**
     * Registers a listener which will be called when all applications are closed and session end page is displayed.
     * @param listener {Function=} - the function to call when session end page is displayed
     * @returns {function(this:classes.ModelHelper)} The unregister handler. Simply call this function to stop listening for session end
     * @publicdoc
     */
    addSessionEndListener(listener) {
      if (this._activeListenersHandlers === null) {
        this._activeListenersHandlers = [];
      }
      if (this._sessionEndListeners === null) {
        this._sessionEndListeners = [];

        const onSessionEndClosed = (event, sender, sessionId) => {
          // for each iteration we recheck if array is defined because modelhelper may have been destroyed in a previous iteration
          for (let i = 0; this._sessionEndListeners && i < this._sessionEndListeners.length; ++i) {
            this._sessionEndListeners[i](sessionId);
          }
        };

        this._activeListenersHandlers.push(
          context.SessionService.onSessionAdded((event, sender, session) => {
            // If null, widget may have been destroyed. We stop to create listeners. Otherwise, we attach new one.
            if (this._activeListenersHandlers) {
              this._activeListenersHandlers.push(session.when(context.constants.baseEvents.displayEnd, onSessionEndClosed));
            }
          })
        );
        const session = context.SessionService.getCurrent();
        if (session) {
          this._activeListenersHandlers.push(session.when(context.constants.baseEvents.displayEnd, onSessionEndClosed));
        }
      }
      this._sessionEndListeners.push(listener);

      return () => {
        if (this._sessionEndListeners) {
          cls.ArrayHelper.remove(this._sessionEndListeners, listener);
        }
      };
    }

    /**
     * Registers a listener which will be called when the current window changes
     * @param listener {Function=} - the function to call when the current window changes
     * @returns {function(this:classes.ModelHelper)} The unregister handler. Simply call this function to stop listening for window changes
     * @publicdoc
     */
    addCurrentWindowChangeListener(listener) {
      if (this._activeListenersHandlers === null) {
        this._activeListenersHandlers = [];
      }
      if (this._currentWindowListeners === null) {
        this._currentWindowListeners = [];

        this._activeListenersHandlers.push(
          context.HostService.onCurrentWindowChange(
            /**
             * @param {classes.Event} event event object
             * @param _sender
             * @param {classes.WindowWidget} windowWidget data
             */
            (event, _sender, windowWidget) => {
              let windowNode = null;
              let appHash = null;
              while (windowWidget) {
                if (windowWidget._appHash !== undefined) {
                  appHash = windowWidget._appHash;
                  break;
                }
                windowWidget = windowWidget.getParentWidget();
              }
              if (appHash !== null) {
                let app = null;
                const session = context.SessionService.getCurrent();
                for (let i = 0; i < session._applications.length; ++i) {
                  const a = session._applications[i];
                  if (a.applicationHash === appHash) {
                    app = a;
                    break;
                  }
                }
                windowNode = app?.model.getNode(windowWidget._auiTag);
              }
              // for each iteration we recheck if array is defined because modelhelper may have been destroyed in a previous iteration
              for (let i = 0; this._currentWindowListeners && i < this._currentWindowListeners.length; ++i) {
                this._currentWindowListeners[i](windowNode);
              }
            }
          )
        );
      }
      this._currentWindowListeners.push(listener);

      return () => {
        if (this._currentWindowListeners) {
          cls.ArrayHelper.remove(this._currentWindowListeners, listener);
        }
      };
    }

    /**
     * Registers a listener which will be called when any DVM answer is received.
     * You can update your widget in the provided callback
     * This listening method is general to all started applications. If your UI updates are heavy, prefer more fine-grained update notification mechanisms
     * @param {Function=} listener - the function to call when a new application is started
     * @returns {function(this:classes.ModelHelper)} The unregister handler. Simply call this function to stop listening for new applications
     * @publicdoc
     */
    addAuiUpdateListener(listener) {
      if (this._activeListenersHandlers === null) {
        this._activeListenersHandlers = [];
      }
      if (this._auiUpdateListeners === null) {
        this._auiUpdateListeners = [];

        this._activeListenersHandlers.push(
          context.SessionService.onSessionAdded((event, session) => {
            // If null, widget may have been destroyed. We stop to create listeners. Otherwise, we attach new one.
            if (this._activeListenersHandlers) {
              this._activeListenersHandlers.push(session.getNavigationManager().onApplicationAdded(this._onApplicationAdded.bind(this)));
            }
          })
        );
        const currentSession = context.SessionService.getCurrent();
        if (currentSession) {
          for (const element of currentSession._applications) {
            this._activeListenersHandlers.push(element.dvm.onOrdersManaged(this._dispatchUpdate.bind(this)));
          }
        }
      }
      this._auiUpdateListeners.push(listener);
      return () => {
        if (this._auiUpdateListeners) {
          cls.ArrayHelper.remove(this._auiUpdateListeners, listener);
        }
      };
    }

    _dispatchUpdate(event, src, data) {
      // for each iteration we recheck if array is defined because modelhelper may have been destroyed in a previous iteration
      for (let i = 0; this._auiUpdateListeners && i < this._auiUpdateListeners.length; ++i) {
        this._auiUpdateListeners[i](src, data);
      }
    }

    _onApplicationAdded(event, app) {
      if (this._activeListenersHandlers) {
        this._activeListenersHandlers.push(app.dvm.onOrdersManaged(this._dispatchUpdate.bind(this)));
      }
    }

    /**
     * Get the current application
     * @returns {?classes.VMApplication} the currently visible application or null if it cannot be found.
     * @publicdoc
     */
    getCurrentApplication() {
      const session = context.SessionService.getCurrent();
      if (session) {
        return session.getCurrentApplication();
      }
      return null;
    }

    /**
     * Get the widget application
     * @returns {?classes.VMApplication} the VM application to which this widget is attached. null if the widget isn't below a VM application
     * @publicdoc
     */
    getApplication() {
      const session = context.SessionService.getCurrent();
      if (session) {
        for (const app of session.getApplications()) {
          if (app.applicationHash === this._widget._appHash) {
            return app;
          }
        }
      }
      return null;
    }

    /**
     * Get a node with its id
     * @param {number} idRef - the VM id of the node to return
     * @returns {?classes.NodeBase} the requested node or null if it couldn't be found
     * @publicdoc
     */
    getNode(idRef) {
      const app = this.getApplication();
      if (app) {
        return app.model.getNode(idRef);
      }
      return null;
    }

    /**
     * Get the UserInterface Node
     * @returns {?classes.NodeBase} the UserInterface node of the current application or null if it couldn't be found
     * @publicdoc
     */
    getUserInterfaceNode() {
      return this.getNode(0);
    }

    /**
     * Get Anchor Node of the widget
     * @returns {?classes.NodeBase} the AUI anchor node of this widget. Generally the corresponding node or the node holding the displayed value
     * @publicdoc
     */
    getAnchorNode() {
      if (this._widget._auiTag !== null) {
        const app = this.getApplication();
        if (app) {
          return app.model.getNode(this._widget._auiTag);
        }
      }
      return null;
    }

    /**
     * Applies only to entry fields (FormField, Matrix or TableColumn)
     * @returns {?classes.NodeBase} the field node corresponding to the widget or null if it doesn't apply
     * @publicdoc
     */
    getFieldNode() {
      const anchorNode = this.getAnchorNode();
      if (anchorNode) {
        if (anchorNode.getTag() === "FormField") {
          return anchorNode;
        } else if (anchorNode.getTag() === "Value") {
          return anchorNode.getParentNode().getParentNode();
        }
      }
      return null;
    }

    /**
     * Applies only to entry fields (FormField, Matrix or TableColumn)
     * A decoration node is the node holding visual information (Edit, CheckBox, ComboBox, etc...)
     * @returns {?classes.NodeBase} the decoration node corresponding to the widget or null if it doesn't apply
     * @publicdoc
     */
    getDecorationNode() {
      const fieldNode = this.getFieldNode();
      if (fieldNode) {
        return fieldNode.getChildren()[0];
      }
      return null;
    }

    /**
     * Try to find & execute an action by name (search in the active Dialog/Menu of the current Window).
     * Note that the focused widget with pending changes will send its value to the VM
     * @param {string} name of the action
     * @returns {boolean} true if an action has been found and executed, false otherwise.
     * @publicdoc
     */
    executeActionByName(name) {
      const currentApp = this.getCurrentApplication();
      return (
        currentApp?.action.executeActionByName(name, {
          sendValue: true
        }) !== null
      );
    }
  }

  cls.ModelHelper = ModelHelper;
});
